import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_SIGNATURE } from "../signature.queries";
import { SignatureMode } from "../../../__generated__/globalTypes";
import {
  CreateSignatureMutation,
  CreateSignatureMutationVariables,
} from "../../../__generated__/CreateSignatureMutation";

interface ISignParams {
  orderId: number;
  imagePng: string;
  signerName: string;
  signerRole: string;
  signerEmail?: string | null;
  /** Empreinte du contenu signé, pour tracer ce qui a été accepté */
  documentHashSha256: string;
  mode?: SignatureMode;
}

/**
 * Signature d'une commande depuis le bon de commande.
 * Contrairement à useCreateSignature (basé sur useForm), la signature
 * provient d'un canvas : on passe les valeurs directement.
 */
export const useSignOrder = () => {
  const [mutate, { loading }] = useMutation<
    CreateSignatureMutation,
    CreateSignatureMutationVariables
  >(CREATE_SIGNATURE, {
    refetchQueries: "active",
  });

  const sign = async ({
    orderId,
    imagePng,
    signerName,
    signerRole,
    signerEmail,
    documentHashSha256,
    mode = SignatureMode.InPerson,
  }: ISignParams) => {
    if (loading) return false;
    try {
      const { data } = await mutate({
        variables: {
          input: {
            orderId,
            imagePng,
            signerName,
            signerRole,
            signerEmail: signerEmail || null,
            documentHashSha256,
            mode,
            signedAt: new Date().toISOString(),
            userAgent: navigator.userAgent,
          },
        },
      });

      if (data?.createSignature?.ok) {
        toast.success("Bon de commande signé");
        return true;
      }
      throw Error(data?.createSignature?.error ?? "Error");
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
      return false;
    }
  };

  return { sign, loading };
};
